import {
  ConversationType,
  IConversation,
  IConversationMessageContent,
} from '@/models/Conversation'
import { ConversationsService } from '@/service/ConversationsService'
import { SocketService } from '@/service/SocketsService'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'

import { ISocketMessage } from '@/models/Sockets'
import { IUser } from '@/models/Users'
import { PaginationParams } from '@/types/PaginationParams'
import { isGokollab } from '@ghl/clientportal-core/init'
import { Socket } from 'socket.io-client'
import { v7 as uuidv7 } from 'uuid'
import { reactive } from 'vue'
import { GroupState } from './groups'
import { UserState } from './users'

export class ConversationsState {
  private maxChats = 2
  state = reactive({
    conversations: [] as IConversation[],
    conversationStatus: new Map<string, string>(), // map of conversationId and status (typing, not typing, online etc)
    conversationSuggestions: [] as IUser[],
    isConversationNextPageAvailable: true,
    isConversationSuggestionNextPageAvailable: true,
    unReadConversationCount: 0,
    isSocketConnected: false,
    activeChatWindows: [] as any[],
    socket: null as Socket | null,
  })

  async openNewChatWindow(
    conversationId: string,
    conversation?: IConversation
  ): Promise<void> {
    const existingChatIndex = this.state.activeChatWindows.findIndex(
      chat => chat._id === conversationId
    )
    if (existingChatIndex !== -1) {
      this.state.activeChatWindows[existingChatIndex].isOpen = true
      this.state.activeChatWindows[existingChatIndex].minimized = false
    } else {
      if (this.state.activeChatWindows.length >= this.maxChats) {
        this.state.activeChatWindows.pop() // Remove the oldest chat if max limit reached
      }
      if (!conversation) {
        conversation = await this.getConversationById(conversationId)
      }
      this.state.activeChatWindows.unshift({
        ...conversation,
        isOpen: true,
        minimized: false,
      })
    }
  }

  clearConversations(): void {
    this.state.conversations = []
    this.state.isConversationNextPageAvailable = true
  }
  clearConversationsSuggestions(): void {
    this.state.conversationSuggestions = []
    this.state.isConversationSuggestionNextPageAvailable = true
  }

  async createConversation(contactId: string): Promise<IConversation> {
    try {
      const response = await ConversationsService.createConversation(
        [contactId],
        ConversationType.DM
      )
      trackCustomGAEvents(GAEventTypes.CHAT_CREATED, {
        locationId: GroupState.state.groupData.locationId,
      })
      // add the new conversation to the conversations array
      response.newChat = true
      // check if the conversation is already in the state
      const existingConversation = this.state.conversations.find(
        c => c._id === response._id
      )
      if (!existingConversation) {
        this.state.conversations.unshift(response)
      }
      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async fetchConversationMessages(
    conversationId: string,
    paginationParams: PaginationParams
  ): Promise<any> {
    try {
      const response = await ConversationsService.getConversationHistory(
        conversationId,
        paginationParams
      )
      // find the conversation and add the messages to it
      const conversation = this.state.conversations.find(
        c => c._id === conversationId
      )
      if (conversation) {
        if (!paginationParams?.previousId) {
          conversation.messages = response
        } else {
          conversation.messages = conversation.messages.concat(response)
        }
      }

      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async clearConversation(conversationId?: string): Promise<void> {
    if (!conversationId) {
      return
    }
    const conversation = this.state.conversations.find(
      c => c._id === conversationId
    )
    if (conversation) {
      conversation.messages = []
    }
    await ConversationsService.clearConversation(conversationId)

    // remove the conversation from the state
    this.state.conversations = this.state.conversations.filter(
      c => c._id !== conversationId
    )
  }

  async blockConversation(conversationId?: string): Promise<void> {
    if (!conversationId) {
      return
    }
    const conversation = this.state.conversations.find(
      c => c._id === conversationId
    )
    if (conversation) {
      if (conversation.blockedBy) {
        conversation.blockedBy.push(
          UserState.state.currentUser?.contactId as string
        )
      } else {
        conversation.blockedBy = [
          UserState.state.currentUser?.contactId as string,
        ]
      }
    }
    await ConversationsService.blockConversation(conversationId)
    trackCustomGAEvents(GAEventTypes.CHAT_BLOCKED, {
      locationId: GroupState.state.groupData.locationId,
    })
  }

  async unblockConversation(conversationId?: string): Promise<void> {
    if (!conversationId) {
      return
    }
    const conversation = this.state.conversations.find(
      c => c._id === conversationId
    )
    if (conversation) {
      conversation.blockedBy = conversation.blockedBy?.filter(
        blockedBy => blockedBy !== UserState.state.currentUser?.contactId
      )
    }
    await ConversationsService.unblockConversation(conversationId)
  }

  async sendMessage(
    conversation: IConversation,
    message: IConversationMessageContent
  ): Promise<void> {
    try {
      const refId = uuidv7()
      // send message to socket
      await SocketService.sendMessage({
        ...message,
        sender:
          isGokollab() && UserState.state.currentUser?.gokollabContactId
            ? UserState.state.currentUser?.gokollabContactId
            : UserState.state.currentUser?.contactId,
        conversationId: conversation._id,
        recipient: conversation.recepients[0].contactId,
        refId,
        isGokollab: isGokollab(),
      })

      const msg = {
        conversationId: conversation._id,
        sender:
          isGokollab() && UserState.state.currentUser?.gokollabContactId
            ? UserState.state.currentUser?.gokollabContactId
            : UserState.state.currentUser?.contactId,
        content: message,
        refId,
      }

      const existingConversation = this.state.conversations.find(
        c => c._id === conversation._id
      )
      if (existingConversation) {
        // add the new message to the conversation
        existingConversation.messages.unshift(msg)
        // existingConversation.lastMessageAt = response.createdAt
        existingConversation.lastReadAt = new Date()
        existingConversation['newChat'] = false
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async pushConversationEvents(
    eventName: string,
    conversation: IConversation
  ): Promise<void> {
    // send message to socket only if the conversation has a recepient that is not deleted
    if (conversation.recepients[0]) {
      await SocketService.sendConversationEvents({
        eventType: eventName,
        conversationId: conversation._id,
        contactId:
          isGokollab() && UserState.state.currentUser?.gokollabContactId
            ? UserState.state.currentUser?.gokollabContactId
            : UserState.state.currentUser?.contactId,
        recipientId: conversation.recepients[0].contactId,
      })
    }
  }

  async markConversationAsRead(conversationId?: string): Promise<void> {
    try {
      if (!conversationId) {
        return
      }
      ConversationsService.markConversationAsRead(conversationId)
      // mark notifications in the state as read
      if (!conversationId) {
        // this.state.conversations.forEach(n => (n.isRead = true))
        // this.state.unReadConversationCount = 0
      } else {
        const conversation = this.state.conversations.find(
          c => c._id === conversationId
        )
        if (conversation) {
          conversation.lastReadAt = new Date()
        }
        if (this.state.unReadConversationCount > 0) {
          this.state.unReadConversationCount -= 1
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getConversations(paginationObject?: PaginationParams): Promise<void> {
    try {
      if (!this.state.isConversationNextPageAvailable) {
        return
      }

      if (
        !paginationObject?.previousId &&
        this.state.conversations.length > 1
      ) {
        return
      } else {
        if (paginationObject?.previousId) {
          const previousIdIndex = this.state.conversations.findIndex(
            conversation =>
              conversation.lastMessageAt === paginationObject?.previousId
          )
          if (previousIdIndex != this.state.conversations.length - 1) {
            return
          }
        }
      }

      const conversations = await ConversationsService.getConversations({
        limit: paginationObject?.limit,
        lastMessageAtCursor: paginationObject?.previousId,
      })

      if (!paginationObject?.previousId) {
        this.state.conversations = []
      }

      conversations.forEach(c => {
        if (
          !this.state.conversations.find(
            conversation => conversation._id == c._id
          )
        ) {
          this.state.conversations.push(c)
        }
      })

      if (paginationObject?.limit) {
        if (conversations.length < paginationObject.limit) {
          this.state.isConversationNextPageAvailable = false
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getConversationById(
    conversationId: string
  ): Promise<IConversation | undefined> {
    try {
      let conversation = this.state.conversations.find(
        conversation => conversation._id === conversationId
      )
      if (conversation) {
        return conversation
      }

      conversation = await ConversationsService.getConversationById(
        conversationId
      )

      if (!this.state.conversations.find(c => c._id == conversation?._id)) {
        this.state.conversations.push(conversation!)
      }
      return conversation
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getConversationSuggestions(
    searchText: string,
    paginationParams: PaginationParams
  ): Promise<void> {
    try {
      if (!this.state.isConversationSuggestionNextPageAvailable) {
        return
      }
      const expctedSuggestionLength =
        paginationParams.limit! * paginationParams.pageNo! +
        paginationParams.limit!

      if (
        this.state.conversationSuggestions.length >= expctedSuggestionLength
      ) {
        return
      }

      const conversations =
        await ConversationsService.getConversationSuggestions({
          ...paginationParams,
          searchText,
        })
      if (paginationParams?.pageNo !== 1) {
        this.state.conversationSuggestions =
          this.state.conversationSuggestions.concat(conversations)
      } else {
        this.state.conversationSuggestions = conversations
      }
      if (conversations.length < (paginationParams.limit ?? 10)) {
        this.state.isConversationSuggestionNextPageAvailable = false
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }
  async getUnreadConversationsCount(): Promise<void> {
    try {
      const conversations = await ConversationsService.getConversations({
        limit: 10,
        unread: true,
      })

      this.state.unReadConversationCount = conversations.length
    } catch (error) {
      console.error(error)
      throw error
    }
  }
  async syncUnreadConversations(): Promise<void> {
    let unreadConversationsCount = 0
    ConversationState.state.conversations.forEach(async conversation => {
      if (conversation.lastReadAt) {
        const conversationLastRead = new Date(conversation.lastReadAt)
        const lastMessage = new Date(conversation.messages[0].createdAt)
        if (conversationLastRead.getTime() < lastMessage.getTime()) {
          unreadConversationsCount++
        }
      } else {
        unreadConversationsCount++
      }
    })
    this.state.unReadConversationCount = unreadConversationsCount
  }
  async handleSocketMessage(message: ISocketMessage): Promise<void> {
    const { event, payload } = message
    switch (event) {
      case 'receiveMessage': {
        const conversation = ConversationState.state.conversations.find(
          c => c._id === payload.conversationId
        )

        if (!conversation) {
          // fetch conversation by id
          await ConversationState.getConversationById(payload.conversationId)
          // conversation is fetched from server then dont need to push the received message to the conversation
          // because new conversation will have latest 10 messages from the DB
        } else {
          ConversationState.removeTypingSignal(conversation)
          conversation.messages.unshift(payload)
          conversation.lastMessageAt = payload.createdAt
          conversation['newChat'] = false
        }
        ConversationState.syncUnreadConversations()
        break
      }
      case 'messageSent': {
        const existingConversation = ConversationState.state.conversations.find(
          c => c._id === payload.conversationId
        )

        // else condition is not expected because this event is response to sendMessage event to update
        // message id and other details in the client side after the message is sent to the server
        // so conversation will always be found
        if (existingConversation) {
          // replace that message with the new message
          const messageIndex = existingConversation.messages.findIndex(
            m => m.refId === payload.refId
          )
          if (payload.status === 'failed') {
            existingConversation.messages[messageIndex].status = 'failed'
            existingConversation.lastMessageAt = new Date().toDateString()
            return
          }
          if (messageIndex > -1) {
            delete payload.refId
            existingConversation.messages[messageIndex] = payload
          } else {
            existingConversation.messages.unshift(payload)
          }
          existingConversation.lastMessageAt = payload.createdAt
          existingConversation['newChat'] = false

          // add time out of 1 sec to mark the conversation as read
          setTimeout(async () => {
            await ConversationState.markConversationAsRead(
              existingConversation._id
            )
          }, 500)
        }
        break
      }
      case 'conversationEvents': {
        ConversationState.state.conversationStatus.set(
          payload.conversationId,
          payload.eventType
        )
        const existingConversation = ConversationState.state.conversations.find(
          c => c._id === payload.conversationId
        )
        if (existingConversation) {
          if (payload.eventType === 'typing') {
            // check if the typing event is already there in the messages array
            ConversationState.addTypingSignal(
              existingConversation,
              payload.sender
            )
          } else {
            // remove the typing message from the messages array
            ConversationState.removeTypingSignal(existingConversation)
          }
        }
        break
      }
      default:
        console.error('event not handled', event)
        break
    }
  }

  addTypingSignal(conversation: IConversation, sender: string): void {
    // check if the conversation is not blocked
    if (conversation.blockedBy?.length > 0) {
      return
    }
    const typingMessage = conversation.messages.find(m => m._id === 'typing')
    if (!typingMessage) {
      conversation.messages.unshift({
        _id: 'typing',
        sender: sender,
        content: {
          signal: 'typing',
        },
        createdAt: new Date(),
        conversationId: conversation._id,
      })
    }
  }

  removeTypingSignal(conversation: IConversation): void {
    conversation.messages = conversation.messages.filter(
      m => m._id !== 'typing'
    )
  }

  handleSocketConnectionState(state: boolean): void {
    ConversationState.state.isSocketConnected = state
  }
}

export const ConversationState = new ConversationsState()
